import React, { useState, useEffect, useCallback } from 'react';
import {
    Card,
    Table,
    Tag,
    Button,
    Modal,
    Input,
    message,
    Tabs,
    Space,
    Tooltip,
    Badge,
    Descriptions,
    Alert,
    AutoComplete,
    DatePicker,
    Row,
    Col,
    Statistic,
    Empty
} from 'antd';
import {
    SafetyOutlined,
    LockOutlined,
    UnlockOutlined,
    ReloadOutlined,
    SearchOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    WarningOutlined,
    UserOutlined,
    TeamOutlined,
    GlobalOutlined,
    LoginOutlined,
    BarChartOutlined,
    DashboardOutlined
} from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import moment from 'moment';
import debounce from 'lodash/debounce';
import {
    useGetBlockedUsers,
    useGetLoginAttempts,
    useBlockUser,
    useUnblockUser,
    useResetFailedAttempts,
    useGetSecurityConfig,
    useAutocompleteUsernames,
    useGetLoginActivitySummary,
    useGetActiveSessions
} from '../hooks/useUserSecurity';
import { UserSecurityDashboard } from '../components/UserSecurityDashboard';
import LoginAnalytics from '../components/LoginAnalytics';

const { TabPane } = Tabs;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

export const UserSecurityView: React.FC = () => {
    const { dark } = useTheme();
    const [searchUsername, setSearchUsername] = useState('');
    const [autocompleteQuery, setAutocompleteQuery] = useState('');
    const [selectedUser, setSelectedUser] = useState<any>(null);
    const [blockModalVisible, setBlockModalVisible] = useState(false);
    const [blockReason, setBlockReason] = useState('');
    const [activeTab, setActiveTab] = useState('0');
    const [dateRange, setDateRange] = useState<[string, string] | null>(null);
    const [summaryPeriod] = useState(30);

    // Hooks
    const { data: blockedUsersResponse, isLoading: blockedLoading, refetch: refetchBlocked } = useGetBlockedUsers();
    const { data: loginAttemptsResponse, isLoading: attemptsLoading, refetch: refetchAttempts } = useGetLoginAttempts(
        searchUsername,
        30,
        dateRange?.[0],
        dateRange?.[1],
        { enabled: !!searchUsername }
    );
    const { data: securityConfigResponse } = useGetSecurityConfig();
    const { data: usernamesResponse } = useAutocompleteUsernames(autocompleteQuery, 10, { enabled: autocompleteQuery.length >= 2 });
    const { data: summaryResponse } = useGetLoginActivitySummary(searchUsername, summaryPeriod);
    const { data: sessionsResponse, isLoading: sessionsLoading } = useGetActiveSessions();

    // Extract data from response (Orval returns data directly)
    const blockedUsers = Array.isArray(blockedUsersResponse) ? blockedUsersResponse : [];
    const loginAttempts = Array.isArray(loginAttemptsResponse) ? loginAttemptsResponse : [];
    const securityConfig = securityConfigResponse;
    const usernames = Array.isArray(usernamesResponse) ? usernamesResponse : [];
    const summary = summaryResponse;
    const activeSessions = Array.isArray(sessionsResponse) ? sessionsResponse : [];
    const blockUserMutation = useBlockUser();
    const unblockUserMutation = useUnblockUser();
    const resetAttemptsMutation = useResetFailedAttempts();

    // Debounced autocomplete search
    const debouncedSearch = useCallback(
        debounce((value: string) => {
            setAutocompleteQuery(value);
        }, 300),
        []
    );

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    // Handlers
    const handleBlock = async () => {
        if (!selectedUser || !blockReason.trim()) {
            message.warning('Please provide a reason for blocking');
            return;
        }

        try {
            await blockUserMutation.mutateAsync({
                data: {
                    userId: selectedUser.userId,
                    reason: blockReason
                }
            });
            message.success(`User ${selectedUser.userId} has been blocked`);
            setBlockModalVisible(false);
            setBlockReason('');
            setSelectedUser(null);
            refetchBlocked();
        } catch (error: any) {
            message.error(error.message || 'Failed to block user');
        }
    };

    const handleUnblock = async (userId: string) => {
        try {
            await unblockUserMutation.mutateAsync({ userId });
            message.success(`User ${userId} has been unblocked`);
            refetchBlocked();
        } catch (error: any) {
            message.error(error.message || 'Failed to unblock user');
        }
    };

    const handleResetAttempts = async (userId: string) => {
        try {
            await resetAttemptsMutation.mutateAsync({ userId });
            message.success(`Failed attempts reset for user ${userId}`);
            refetchBlocked();
        } catch (error: any) {
            message.error(error.message || 'Failed to reset attempts');
        }
    };

    const showBlockModal = (user: any) => {
        setSelectedUser(user);
        setBlockModalVisible(true);
    };

    const handleDateRangeChange = (dates: any, dateStrings: [string, string]) => {
        if (dates) {
            setDateRange(dateStrings);
        } else {
            setDateRange(null);
        }
        if (searchUsername) {
            refetchAttempts();
        }
    };

    const handleUsernameSelect = (value: string) => {
        setSearchUsername(value);
    };

    const handleSearchChange = (value: string) => {
        setSearchUsername(value);
        debouncedSearch(value);
    };

    // Columns for blocked users table
    const blockedUsersColumns = [
        {
            title: 'User ID',
            dataIndex: 'userId',
            key: 'userId',
            render: (text: string) => <strong>{text}</strong>,
        },
        {
            title: 'Full Name',
            dataIndex: 'fullName',
            key: 'fullName',
        },
        {
            title: 'Block Type',
            dataIndex: 'blockedByAdmin',
            key: 'blockedByAdmin',
            render: (isAdmin: boolean) => (
                <Tag color={isAdmin ? 'red' : 'orange'} icon={<LockOutlined />}>
                    {isAdmin ? 'Admin Block' : 'Auto Block'}
                </Tag>
            ),
        },
        {
            title: 'Failed Attempts',
            dataIndex: 'failedLoginAttempts',
            key: 'failedLoginAttempts',
            render: (count: number) => (
                <Badge count={count} showZero style={{ backgroundColor: count >= 5 ? '#f5222d' : '#faad14' }} />
            ),
        },
        {
            title: 'Blocked At',
            dataIndex: 'blockedAt',
            key: 'blockedAt',
            render: (date: string) => moment(date).format('DD/MM/YYYY HH:mm'),
        },
        {
            title: 'Reason',
            dataIndex: 'blockReason',
            key: 'blockReason',
            ellipsis: true,
            render: (reason: string) => (
                <Tooltip title={reason}>
                    <span>{reason || 'N/A'}</span>
                </Tooltip>
            ),
        },
        {
            title: 'Auto Unblock',
            dataIndex: 'canAutoUnblock',
            key: 'canAutoUnblock',
            render: (canAuto: boolean, record: any) => {
                if (canAuto && record.estimatedUnblockTime) {
                    const unlockTime = moment(record.estimatedUnblockTime);
                    const now = moment();
                    const isOverdue = unlockTime.isBefore(now);

                    return (
                        <Tooltip title={`Estimated unlock: ${unlockTime.format('DD/MM/YYYY HH:mm')}`}>
                            <Tag color={isOverdue ? 'green' : 'blue'} icon={<ClockCircleOutlined />}>
                                {isOverdue ? 'Eligible Now' : unlockTime.fromNow()}
                            </Tag>
                        </Tooltip>
                    );
                }
                return <Tag color="default">Manual Only</Tag>;
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: any) => (
                <Space>
                    <Button
                        type="primary"
                        size="small"
                        icon={<UnlockOutlined />}
                        onClick={() => handleUnblock(record.userId)}
                        loading={unblockUserMutation.isPending}
                    >
                        Unblock
                    </Button>
                    {record.failedLoginAttempts > 0 && (
                        <Button
                            size="small"
                            icon={<ReloadOutlined />}
                            onClick={() => handleResetAttempts(record.userId)}
                            loading={resetAttemptsMutation.isPending}
                        >
                            Reset Attempts
                        </Button>
                    )}
                </Space>
            ),
        },
    ];

    // Columns for login attempts table with block action
    const loginAttemptsColumns = [
        {
            title: 'Username',
            dataIndex: 'username',
            key: 'username',
            render: (username: string) => (
                <Space>
                    <UserOutlined />
                    <strong>{username}</strong>
                </Space>
            ),
        },
        {
            title: 'Time',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date: string) => moment(date).format('DD/MM/YYYY HH:mm:ss'),
            sorter: (a: any, b: any) => moment(a.createdAt).unix() - moment(b.createdAt).unix(),
        },
        {
            title: 'Status',
            dataIndex: 'success',
            key: 'success',
            render: (success: boolean) => (
                <Tag color={success ? 'success' : 'error'} icon={success ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
                    {success ? 'Success' : 'Failed'}
                </Tag>
            ),
            filters: [
                { text: 'Success', value: true },
                { text: 'Failed', value: false },
            ],
            onFilter: (value: any, record: any) => record.success === value,
        },
        {
            title: 'IP Address',
            dataIndex: 'ipAddress',
            key: 'ipAddress',
        },
        {
            title: 'Failure Reason',
            dataIndex: 'failureReason',
            key: 'failureReason',
            ellipsis: true,
            render: (reason: string) => (
                <Tooltip title={reason}>
                    <span>{reason || '-'}</span>
                </Tooltip>
            ),
        },
        {
            title: 'User Agent',
            dataIndex: 'userAgent',
            key: 'userAgent',
            ellipsis: true,
            render: (agent: string) => (
                <Tooltip title={agent}>
                    <span>{agent || '-'}</span>
                </Tooltip>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: any) => (
                <Button
                    danger
                    size="small"
                    icon={<LockOutlined />}
                    onClick={() => showBlockModal({ userId: record.username })}
                >
                    Block User
                </Button>
            ),
        },
    ];

    // Columns for active sessions
    const activeSessionsColumns = [
        {
            title: 'Username',
            dataIndex: 'username',
            key: 'username',
            render: (text: string) => (
                <Space>
                    <UserOutlined />
                    <strong>{text}</strong>
                </Space>
            ),
        },
        {
            title: 'IP Address',
            dataIndex: 'ipAddress',
            key: 'ipAddress',
            render: (text: string) => (
                <Space>
                    <GlobalOutlined />
                    {text}
                </Space>
            ),
        },
        {
            title: 'Created At',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date: string) => moment(date).format('DD/MM/YYYY HH:mm:ss'),
        },
        {
            title: 'Last Accessed',
            dataIndex: 'lastAccessedAt',
            key: 'lastAccessedAt',
            render: (date: string) => moment(date).fromNow(),
        },
        {
            title: 'Max Inactive',
            dataIndex: 'maxInactiveInterval',
            key: 'maxInactiveInterval',
            render: (interval: number) => `${interval / 60} min`,
        },
    ];

    return (
        <div className={`p-6 ${dark ? 'bg-gray-900' : 'bg-gray-50'} min-h-screen`}>
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <SafetyOutlined className="text-3xl text-blue-500" />
                            <div>
                                <h1 className={`text-2xl font-bold ${dark ? 'text-white' : 'text-gray-800'}`}>
                                    User Security Management
                                </h1>
                                <p className={`text-sm ${dark ? 'text-gray-400' : 'text-gray-600'}`}>
                                    Monitor login attempts and manage user account security
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Activity Summary Statistics */}
                {summary && (
                    <Card className="mb-6">
                        <Row gutter={16}>
                            <Col xs={24} sm={12} md={6}>
                                <Statistic
                                    title="Total Attempts"
                                    value={summary.totalAttempts}
                                    prefix={<LoginOutlined />}
                                    valueStyle={{ color: dark ? '#fff' : '#000' }}
                                />
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Statistic
                                    title="Successful Logins"
                                    value={summary.successfulLogins}
                                    prefix={<CheckCircleOutlined />}
                                    valueStyle={{ color: '#3f8600' }}
                                />
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Statistic
                                    title="Failed Logins"
                                    value={summary.failedLogins}
                                    prefix={<CloseCircleOutlined />}
                                    valueStyle={{ color: '#cf1322' }}
                                />
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Statistic
                                    title="Unique Users"
                                    value={summary.uniqueUsers}
                                    prefix={<TeamOutlined />}
                                    valueStyle={{ color: dark ? '#fff' : '#000' }}
                                />
                            </Col>
                        </Row>
                        {searchUsername && (
                            <div className="mt-4">
                                <p className={`text-sm ${dark ? 'text-gray-400' : 'text-gray-600'}`}>
                                    Showing statistics for: <strong>{searchUsername}</strong> (Last {summaryPeriod} days)
                                </p>
                            </div>
                        )}
                    </Card>
                )}

                {/* Security Configuration Alert */}
                {securityConfig && (
                    <Alert
                        message="Security Configuration"
                        description={
                            <Descriptions size="small" column={4}>
                                <Descriptions.Item label="Max Failed Attempts">
                                    {securityConfig.maxFailedAttempts}
                                </Descriptions.Item>
                                <Descriptions.Item label="Lock Duration">
                                    {securityConfig.lockDurationHours} hours
                                </Descriptions.Item>
                                <Descriptions.Item label="Attempt Window">
                                    {securityConfig.attemptWindowMinutes} minutes
                                </Descriptions.Item>
                                <Descriptions.Item label="Status">
                                    <Tag color={securityConfig.enabled ? 'success' : 'default'}>
                                        {securityConfig.enabled ? 'Enabled' : 'Disabled'}
                                    </Tag>
                                </Descriptions.Item>
                            </Descriptions>
                        }
                        type="info"
                        showIcon
                        className="mb-6"
                    />
                )}

                <Tabs activeKey={activeTab} onChange={setActiveTab}>
                    {/* Dashboard Tab */}
                    <TabPane
                        tab={
                            <span>
                                <DashboardOutlined />
                                Dashboard
                            </span>
                        }
                        key="0"
                    >
                        <div className="space-y-6">
                            <UserSecurityDashboard />
                        </div>
                    </TabPane>

                    {/* Blocked Users Tab */}
                    <TabPane
                        tab={
                            <span>
                                <LockOutlined />
                                Blocked Users {blockedUsers && `(${blockedUsers.length})`}
                            </span>
                        }
                        key="1"
                    >
                        <Card>
                            <Table
                                columns={blockedUsersColumns}
                                dataSource={blockedUsers}
                                loading={blockedLoading}
                                rowKey="userId"
                                pagination={{ pageSize: 10 }}
                            />
                        </Card>
                    </TabPane>

                    {/* Login Attempts Tab */}
                    <TabPane
                        tab={
                            <span>
                                <SearchOutlined />
                                Login Attempts
                            </span>
                        }
                        key="2"
                    >
                        <Card>
                            <Space direction="vertical" style={{ width: '100%' }} size="large">
                                <Row gutter={16}>
                                    <Col xs={24} md={12}>
                                        <AutoComplete
                                            value={searchUsername}
                                            // @ts-ignore
                                            options={usernames.map(u => ({ value: u }))}
                                            onSelect={handleUsernameSelect}
                                            onChange={handleSearchChange}
                                            onSearch={handleSearchChange}
                                            placeholder="Type username to search (autocomplete enabled)"
                                            style={{ width: '100%' }}
                                        >
                                            <Input
                                                prefix={<SearchOutlined />}
                                                suffix={
                                                    searchUsername && (
                                                        <Button
                                                            type="link"
                                                            size="small"
                                                            onClick={() => {
                                                                setSearchUsername('');
                                                                setAutocompleteQuery('');
                                                            }}
                                                        >
                                                            Clear
                                                        </Button>
                                                    )
                                                }
                                            />
                                        </AutoComplete>
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <RangePicker
                                            onChange={handleDateRangeChange}
                                            style={{ width: '100%' }}
                                            placeholder={['Start Date', 'End Date']}
                                            disabled={!searchUsername}
                                        />
                                    </Col>
                                </Row>
                                {searchUsername && (
                                    <Alert
                                        message={
                                            <span>
                                                Showing login attempts for: <strong>{searchUsername}</strong>
                                                {dateRange ? (
                                                    <> from <strong>{dateRange[0]}</strong> to <strong>{dateRange[1]}</strong></>
                                                ) : (
                                                    <> (Last 30 days)</>
                                                )}
                                            </span>
                                        }
                                        type="info"
                                        showIcon
                                    />
                                )}
                            </Space>

                            <div className="mt-4">
                                {searchUsername ? (
                                    <Table
                                        columns={loginAttemptsColumns}
                                        dataSource={loginAttempts}
                                        loading={attemptsLoading}
                                        rowKey="id"
                                        pagination={{ pageSize: 20 }}
                                    />
                                ) : (
                                    <div className="text-center py-12">
                                        <SearchOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
                                        <p className={`mt-4 ${dark ? 'text-gray-400' : 'text-gray-600'}`}>
                                            Start typing a username to search login attempts (autocomplete enabled)
                                        </p>
                                    </div>
                                )}
                            </div>
                        </Card>
                    </TabPane>

                    {/* Active Sessions Tab */}
                    <TabPane
                        tab={
                            <span>
                                <ClockCircleOutlined />
                                Active Sessions
                            </span>
                        }
                        key="3"
                    >
                        <Card>
                            {activeSessions.length > 0 ? (
                                <Table
                                    columns={activeSessionsColumns}
                                    dataSource={activeSessions}
                                    loading={sessionsLoading}
                                    rowKey="sessionId"
                                    pagination={{ pageSize: 20 }}
                                />
                            ) : (
                                <Empty
                                    description={
                                        <span className={dark ? 'text-gray-400' : 'text-gray-600'}>
                                            {sessionsLoading
                                                ? 'Loading active sessions...'
                                                : 'No active sessions found. Spring Session integration required for this feature.'}
                                        </span>
                                    }
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                />
                            )}
                        </Card>
                    </TabPane>

                    {/* Analytics Tab */}
                    <TabPane
                        tab={
                            <span>
                                <BarChartOutlined />
                                Analytics & Reports
                            </span>
                        }
                        key="4"
                    >
                        <LoginAnalytics />
                    </TabPane>
                </Tabs>

                {/* Block User Modal */}
                <Modal
                    title={
                        <span>
                            <WarningOutlined className="text-red-500 mr-2" />
                            Block User: {selectedUser?.userId}
                        </span>
                    }
                    open={blockModalVisible}
                    onOk={handleBlock}
                    onCancel={() => {
                        setBlockModalVisible(false);
                        setBlockReason('');
                        setSelectedUser(null);
                    }}
                    confirmLoading={blockUserMutation.isPending}
                    okText="Block User"
                    okButtonProps={{ danger: true }}
                >
                    <div className="py-4">
                        <p className="mb-4">
                            Please provide a reason for blocking this user. This will be visible to the user.
                        </p>
                        <TextArea
                            rows={4}
                            placeholder="Enter reason for blocking (e.g., Security policy violation, Suspicious activity, etc.)"
                            value={blockReason}
                            onChange={(e) => setBlockReason(e.target.value)}
                            maxLength={500}
                            showCount
                        />
                    </div>
                </Modal>
            </div>
        </div>
    );
};
